
<script setup>
import { ref, computed, watch } from 'vue'
import { 
  CogIcon, 
  SwatchIcon, 
  PhotoIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SparklesIcon,
  EyeDropperIcon,
  ArrowPathIcon,
  ServerIcon,
  DevicePhoneMobileIcon,
  BoltIcon,
  CloudIcon
} from '@heroicons/vue/24/outline'

// Props & Emits
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      size: 300,
      margin: 10,
      errorCorrectionLevel: 'M',
      darkColor: '#000000',
      lightColor: '#ffffff',
      format: 'png',
      source: 'auto'
    })
  },
  showAdvanced: {
    type: Boolean,
    default: false
  },
  generationSource: {
    type: String,
    default: 'auto'
  }
})

const emit = defineEmits(['update:modelValue'])

// Local reactive state
const localOptions = ref({
  size: props.modelValue.size || 300,
  margin: props.modelValue.margin || 10,
  errorCorrectionLevel: props.modelValue.errorCorrectionLevel || 'M',
  darkColor: props.modelValue.darkColor || '#000000',
  lightColor: props.modelValue.lightColor || '#ffffff',
  format: props.modelValue.format || 'png',
  source: props.modelValue.source || 'auto'
})

const activeSection = ref('basic') // 'basic', 'colors', 'advanced', 'generation'

// Available options
const sizeOptions = [
  { value: 128, label: '128×128', description: 'Very Small', category: 'basic', speed: 'fast' },
  { value: 256, label: '256×256', description: 'Small', category: 'basic', speed: 'fast' },
  { value: 300, label: '300×300', description: 'Medium (Recommended)', category: 'basic', speed: 'fast' },
  { value: 512, label: '512×512', description: 'Large', category: 'styled', speed: 'medium' },
  { value: 1024, label: '1024×1024', description: 'Extra Large', category: 'styled', speed: 'slow' },
  { value: 2048, label: '2048×2048', description: 'Print Quality', category: 'styled', speed: 'slow' }
]

const marginOptions = [
  { value: 0, label: '0', description: 'No margin (not recommended)' },
  { value: 1, label: '1', description: 'Minimal' },
  { value: 4, label: '4', description: 'Small' },
  { value: 10, label: '10', description: 'Medium (Recommended)' },
  { value: 20, label: '20', description: 'Large' }
]

const errorCorrectionOptions = [
  { 
    value: 'L', 
    label: 'Low (7%)', 
    description: 'Fastest generation, least error recovery',
    icon: '🟢',
    recommended: false,
    frontendSupport: 'excellent',
    backendSupport: 'excellent'
  },
  { 
    value: 'M', 
    label: 'Medium (15%)', 
    description: 'Balanced speed and recovery (Recommended)',
    icon: '🟡',
    recommended: true,
    frontendSupport: 'excellent',
    backendSupport: 'excellent'
  },
  { 
    value: 'Q', 
    label: 'Quartile (25%)', 
    description: 'Good error recovery',
    icon: '🟠',
    recommended: false,
    frontendSupport: 'good',
    backendSupport: 'excellent'
  },
  { 
    value: 'H', 
    label: 'High (30%)', 
    description: 'Best error recovery, supports logos/icons',
    icon: '🔴',
    recommended: false,
    frontendSupport: 'limited',
    backendSupport: 'excellent'
  }
]

const formatOptions = [
  { 
    value: 'png', 
    label: 'PNG', 
    description: 'Best quality, transparent background', 
    recommended: true,
    frontendSupport: 'excellent',
    backendSupport: 'excellent',
    fileSize: 'large'
  },
  { 
    value: 'jpeg', 
    label: 'JPEG', 
    description: 'Smaller file, no transparency', 
    recommended: false,
    frontendSupport: 'good',
    backendSupport: 'excellent',
    fileSize: 'medium'
  },
  { 
    value: 'webp', 
    label: 'WebP', 
    description: 'Modern format, excellent compression', 
    recommended: false,
    frontendSupport: 'limited',
    backendSupport: 'good',
    fileSize: 'small'
  },
  { 
    value: 'svg', 
    label: 'SVG', 
    description: 'Vector format, infinitely scalable', 
    recommended: false,
    frontendSupport: 'none',
    backendSupport: 'excellent',
    fileSize: 'smallest'
  }
]

const sourceOptions = [
  {
    value: 'auto',
    label: 'Smart Auto',
    description: 'Automatically choose the best generation method',
    icon: SparklesIcon,
    color: 'purple'
  },
  {
    value: 'frontend',
    label: 'Frontend',
    description: 'Fast, interactive styling with qr-code-styling',
    icon: BoltIcon,
    color: 'green'
  },
  {
    value: 'backend',
    label: 'Backend', 
    description: 'Reliable processing with logo support',
    icon: ServerIcon,
    color: 'blue'
  }
]

const colorPresets = [
  { name: 'Classic', dark: '#000000', light: '#ffffff', category: 'basic' },
  { name: 'Blue', dark: '#1e40af', light: '#dbeafe', category: 'basic' },
  { name: 'Green', dark: '#059669', light: '#d1fae5', category: 'basic' },
  { name: 'Purple', dark: '#7c3aed', light: '#e9d5ff', category: 'basic' },
  { name: 'Red', dark: '#dc2626', light: '#fee2e2', category: 'basic' },
  { name: 'Orange', dark: '#ea580c', light: '#fed7aa', category: 'basic' },
  { name: 'Pink', dark: '#db2777', light: '#fce7f3', category: 'premium' },
  { name: 'Indigo', dark: '#4338ca', light: '#e0e7ff', category: 'premium' }
]

// Computed properties
const estimatedFileSize = computed(() => {
  const baseSize = localOptions.value.size
  const format = localOptions.value.format
  
  let multiplier = 1
  switch (format) {
    case 'png':
      multiplier = 0.8
      break
    case 'jpeg':
      multiplier = 0.3
      break
    case 'webp':
      multiplier = 0.25
      break
    case 'svg':
      multiplier = 0.05
      break
  }
  
  const sizeKB = Math.round((baseSize * baseSize * multiplier) / 1000)
  return sizeKB > 1000 ? `~${(sizeKB / 1000).toFixed(1)} MB` : `~${sizeKB} KB`
})

const contrastRatio = computed(() => {
  const rgb1 = hexToRgb(localOptions.value.darkColor)
  const rgb2 = hexToRgb(localOptions.value.lightColor)
  
  if (!rgb1 || !rgb2) return 0
  
  const l1 = getRelativeLuminance(rgb1)
  const l2 = getRelativeLuminance(rgb2)
  
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  
  return ((lighter + 0.05) / (darker + 0.05)).toFixed(1)
})

const contrastWarning = computed(() => {
  const ratio = parseFloat(contrastRatio.value)
  if (ratio < 3) return { level: 'error', message: 'Very poor contrast - QR may not scan' }
  if (ratio < 7) return { level: 'warning', message: 'Low contrast - may be difficult to scan' }
  return null
})

const generationInfo = computed(() => {
  const source = props.generationSource || localOptions.value.source
  const size = localOptions.value.size
  const format = localOptions.value.format
  
  let speed = 'fast'
  let reliability = 'high'
  let features = []
  
  if (source === 'frontend') {
    speed = size > 512 ? 'medium' : 'fast'
    reliability = 'high'
    features = ['Instant preview', 'Real-time styling', 'Interactive']
  } else if (source === 'backend') {
    speed = size > 1024 ? 'slow' : 'medium'
    reliability = 'excellent'
    features = ['Logo support', 'SVG export', 'Batch processing']
  } else {
    speed = 'adaptive'
    reliability = 'excellent'
    features = ['Smart routing', 'Best performance', 'All features']
  }
  
  return { speed, reliability, features, source }
})

const sections = computed(() => [
  { id: 'basic', label: 'Basic', icon: CogIcon },
  { id: 'generation', label: 'Generation', icon: SparklesIcon },
  { id: 'colors', label: 'Colors', icon: SwatchIcon },
  { id: 'advanced', label: 'Advanced', icon: AdjustmentsHorizontalIcon, show: props.showAdvanced }
].filter(section => section.show !== false))

// Methods
const updateOptions = () => {
  emit('update:modelValue', { ...localOptions.value })
}

const resetToDefaults = () => {
  localOptions.value = {
    size: 300,
    margin: 10,
    errorCorrectionLevel: 'M',
    darkColor: '#000000',
    lightColor: '#ffffff',
    format: 'png',
    source: 'auto'
  }
  updateOptions()
}

const applyColorPreset = (preset) => {
  localOptions.value.darkColor = preset.dark
  localOptions.value.lightColor = preset.light
  updateOptions()
}

const swapColors = () => {
  const temp = localOptions.value.darkColor
  localOptions.value.darkColor = localOptions.value.lightColor
  localOptions.value.lightColor = temp
  updateOptions()
}

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

const getRelativeLuminance = (rgb) => {
  const rsRGB = rgb.r / 255
  const gsRGB = rgb.g / 255
  const bsRGB = rgb.b / 255
  
  const r = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4)
  const g = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4)
  const b = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4)
  
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

const getSupportIcon = (support) => {
  switch (support) {
    case 'excellent': return '✅'
    case 'good': return '✅'
    case 'limited': return '⚠️'
    case 'none': return '❌'
    default: return '❓'
  }
}

const getSupportColor = (support) => {
  switch (support) {
    case 'excellent': return 'text-green-600'
    case 'good': return 'text-green-600'
    case 'limited': return 'text-yellow-600'
    case 'none': return 'text-red-600'
    default: return 'text-gray-600'
  }
}

// Watchers
watch(localOptions, updateOptions, { deep: true })

// Watch for external prop changes
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    localOptions.value = { ...newValue }
  }
}, { deep: true })
</script>

<template>
  <div class="space-y-6">
    <!-- Header with Generation Source Info -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-2">
        <CogIcon class="w-5 h-5 text-blue-600" />
        <h3 class="text-lg font-semibold text-gray-800">QR Options</h3>
      </div>
      <div class="flex items-center space-x-3">
        <div class="flex items-center space-x-2 text-sm">
          <div class="w-2 h-2 rounded-full" 
               :class="{
                 'bg-green-400': generationInfo.source === 'frontend',
                 'bg-blue-400': generationInfo.source === 'backend',
                 'bg-purple-400': generationInfo.source === 'auto'
               }">
          </div>
          <span class="text-gray-600">{{ generationInfo.source }}</span>
        </div>
        <button
          @click="resetToDefaults"
          class="text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Reset
        </button>
      </div>
    </div>

    <!-- Section Navigation -->
    <nav class="flex space-x-1 mb-4 bg-gray-100 p-1 rounded-lg">
      <button
        v-for="section in sections"
        :key="section.id"
        @click="activeSection = section.id"
        class="flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors flex-1 justify-center"
        :class="{
          'bg-white text-blue-700 shadow-sm': activeSection === section.id,
          'text-gray-500 hover:text-gray-700': activeSection !== section.id
        }"
      >
        <component :is="section.icon" class="w-4 h-4" />
        <span>{{ section.label }}</span>
      </button>
    </nav>

    <!-- Basic Options -->
    <div v-if="activeSection === 'basic'" class="space-y-6">
      <!-- Size Selection -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-3">
          Image Size
        </label>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <button
            v-for="option in sizeOptions"
            :key="option.value"
            @click="localOptions.size = option.value"
            class="p-3 text-center border rounded-lg transition-all hover:border-blue-300"
            :class="{
              'border-blue-500 bg-blue-50 text-blue-700': localOptions.size === option.value,
              'border-gray-200 text-gray-700': localOptions.size !== option.value
            }"
          >
            <div class="font-medium text-sm">{{ option.label }}</div>
            <div class="text-xs opacity-75">{{ option.description }}</div>
            <div class="flex items-center justify-center space-x-1 mt-1">
              <div class="text-xs" :class="{
                'text-green-600': option.speed === 'fast',
                'text-yellow-600': option.speed === 'medium',
                'text-red-600': option.speed === 'slow'
              }">
                {{ option.speed }}
              </div>
              <div v-if="option.category === 'styled'" class="text-xs text-purple-600">
                ✨
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- Margin Selection -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-3">
          Margin (Quiet Zone)
        </label>
        <div class="grid grid-cols-5 gap-2">
          <button
            v-for="option in marginOptions"
            :key="option.value"
            @click="localOptions.margin = option.value"
            class="p-2 text-center border rounded transition-all hover:border-blue-300"
            :class="{
              'border-blue-500 bg-blue-50 text-blue-700': localOptions.margin === option.value,
              'border-gray-200 text-gray-700': localOptions.margin !== option.value
            }"
          >
            <div class="font-medium">{{ option.label }}</div>
            <div class="text-xs opacity-75">{{ option.description }}</div>
          </button>
        </div>
      </div>

      <!-- Format Selection -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-3">
          Output Format
        </label>
        <div class="grid grid-cols-2 gap-3">
          <button
            v-for="option in formatOptions"
            :key="option.value"
            @click="localOptions.format = option.value"
            class="p-3 text-left border rounded-lg transition-all hover:border-blue-300 relative"
            :class="{
              'border-blue-500 bg-blue-50': localOptions.format === option.value,
              'border-gray-200': localOptions.format !== option.value
            }"
          >
            <div class="font-medium text-sm">{{ option.label }}</div>
            <div class="text-xs text-gray-500">{{ option.description }}</div>
            <div class="flex items-center justify-between mt-2">
              <div class="flex items-center space-x-2">
                <div class="text-xs" :class="getSupportColor(option.frontendSupport)">
                  <DevicePhoneMobileIcon class="w-3 h-3 inline mr-1" />
                  {{ getSupportIcon(option.frontendSupport) }}
                </div>
                <div class="text-xs" :class="getSupportColor(option.backendSupport)">
                  <ServerIcon class="w-3 h-3 inline mr-1" />
                  {{ getSupportIcon(option.backendSupport) }}
                </div>
              </div>
              <div class="text-xs text-gray-500">{{ option.fileSize }}</div>
            </div>
            <div v-if="option.recommended" class="absolute top-2 right-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              ⭐
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- Generation Method -->
    <div v-if="activeSection === 'generation'" class="space-y-6">
      <!-- Source Selection -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-3">
          Generation Method
        </label>
        <div class="space-y-3">
          <div
            v-for="option in sourceOptions"
            :key="option.value"
            @click="localOptions.source = option.value"
            class="p-4 border rounded-lg cursor-pointer transition-all hover:border-gray-300"
            :class="{
              'border-blue-500 bg-blue-50': localOptions.source === option.value,
              'border-gray-200': localOptions.source !== option.value
            }"
          >
            <div class="flex items-center space-x-3">
              <div class="p-2 rounded-lg" :class="{
                'bg-purple-100': option.color === 'purple',
                'bg-green-100': option.color === 'green',
                'bg-blue-100': option.color === 'blue'
              }">
                <component :is="option.icon" class="w-5 h-5" :class="{
                  'text-purple-600': option.color === 'purple',
                  'text-green-600': option.color === 'green',
                  'text-blue-600': option.color === 'blue'
                }" />
              </div>
              <div class="flex-1">
                <div class="font-medium text-gray-900">{{ option.label }}</div>
                <div class="text-sm text-gray-500">{{ option.description }}</div>
              </div>
              <div
                v-if="localOptions.source === option.value"
                class="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center"
              >
                <div class="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Generation Performance Info -->
      <div class="bg-gray-50 p-4 rounded-lg">
        <h4 class="font-medium text-gray-800 mb-3">Performance Overview</h4>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <span class="text-gray-500">Speed:</span>
            <span class="ml-1 font-medium" :class="{
              'text-green-600': generationInfo.speed === 'fast',
              'text-yellow-600': generationInfo.speed === 'medium',
              'text-red-600': generationInfo.speed === 'slow',
              'text-purple-600': generationInfo.speed === 'adaptive'
            }">
              {{ generationInfo.speed }}
            </span>
          </div>
          <div>
            <span class="text-gray-500">Reliability:</span>
            <span class="ml-1 font-medium text-green-600">{{ generationInfo.reliability }}</span>
          </div>
          <div>
            <span class="text-gray-500">File Size:</span>
            <span class="ml-1 font-medium">{{ estimatedFileSize }}</span>
          </div>
        </div>
        <div class="mt-3">
          <span class="text-gray-500 text-sm">Features:</span>
          <div class="flex flex-wrap gap-1 mt-1">
            <span
              v-for="feature in generationInfo.features"
              :key="feature"
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {{ feature }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Colors Section -->
    <div v-if="activeSection === 'colors'" class="space-y-6">
      <div class="flex items-center justify-between mb-3">
        <label class="block text-sm font-medium text-gray-700">
          QR Code Colors
        </label>
        <button
          @click="swapColors"
          class="text-sm text-blue-600 hover:text-blue-800 transition-colors flex items-center space-x-1"
        >
          <ArrowPathIcon class="w-4 h-4" />
          <span>Swap</span>
        </button>
      </div>

      <!-- Color Inputs -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label class="block text-xs text-gray-500 mb-2">Foreground (Dark)</label>
          <div class="flex items-center space-x-2">
            <input
              v-model="localOptions.darkColor"
              type="color"
              class="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              v-model="localOptions.darkColor"
              type="text"
              class="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              placeholder="#000000"
            />
          </div>
        </div>

        <div>
          <label class="block text-xs text-gray-500 mb-2">Background (Light)</label>
          <div class="flex items-center space-x-2">
            <input
              v-model="localOptions.lightColor"
              type="color"
              class="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              v-model="localOptions.lightColor"
              type="text"
              class="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              placeholder="#ffffff"
            />
          </div>
        </div>
      </div>

      <!-- Contrast Analysis -->
      <div class="bg-gray-50 p-4 rounded-lg">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-gray-700">Contrast Analysis</span>
          <span class="text-sm text-gray-600">{{ contrastRatio }}:1</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div
            class="h-2 rounded-full transition-all duration-300"
            :class="{
              'bg-red-500': parseFloat(contrastRatio) < 3,
              'bg-yellow-500': parseFloat(contrastRatio) >= 3 && parseFloat(contrastRatio) < 7,
              'bg-green-500': parseFloat(contrastRatio) >= 7
            }"
            :style="{ width: Math.min(100, (parseFloat(contrastRatio) / 21) * 100) + '%' }"
          ></div>
        </div>
        <div v-if="contrastWarning" class="text-sm" :class="{
          'text-red-600': contrastWarning.level === 'error',
          'text-yellow-600': contrastWarning.level === 'warning'
        }">
          {{ contrastWarning.message }}
        </div>
        <div v-else class="text-sm text-green-600">
          Excellent contrast - QR code will scan reliably
        </div>
      </div>

      <!-- Color Presets -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-3">Color Presets</label>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            v-for="preset in colorPresets"
            :key="preset.name"
            @click="applyColorPreset(preset)"
            class="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors relative"
            :title="preset.name"
          >
            <div class="flex">
              <div 
                class="w-6 h-6 border border-gray-300 rounded-l"
                :style="{ backgroundColor: preset.dark }"
              ></div>
              <div 
                class="w-6 h-6 border border-gray-300 rounded-r"
                :style="{ backgroundColor: preset.light }"
              ></div>
            </div>
            <span class="text-sm font-medium">{{ preset.name }}</span>
            <div v-if="preset.category === 'premium'" class="absolute top-1 right-1 w-2 h-2 bg-purple-400 rounded-full"></div>
          </button>
        </div>
      </div>
    </div>

    <!-- Advanced Options -->
    <div v-if="activeSection === 'advanced'" class="space-y-6">
      <!-- Error Correction Level -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-3">
          Error Correction Level
        </label>
        <div class="space-y-2">
          <div
            v-for="option in errorCorrectionOptions"
            :key="option.value"
            @click="localOptions.errorCorrectionLevel = option.value"
            class="p-4 border rounded-lg cursor-pointer transition-all hover:border-blue-300 relative"
            :class="{
              'border-blue-500 bg-blue-50': localOptions.errorCorrectionLevel === option.value,
              'border-gray-200': localOptions.errorCorrectionLevel !== option.value
            }"
          >
            <div class="flex items-center space-x-3">
              <span class="text-lg">{{ option.icon }}</span>
              <div class="flex-1">
                <div class="font-medium text-gray-900">{{ option.label }}</div>
                <div class="text-sm text-gray-500">{{ option.description }}</div>
                <div class="flex items-center space-x-4 mt-1">
                  <div class="text-xs" :class="getSupportColor(option.frontendSupport)">
                    <DevicePhoneMobileIcon class="w-3 h-3 inline mr-1" />
                    Frontend: {{ getSupportIcon(option.frontendSupport) }}
                  </div>
                  <div class="text-xs" :class="getSupportColor(option.backendSupport)">
                    <ServerIcon class="w-3 h-3 inline mr-1" />
                    Backend: {{ getSupportIcon(option.backendSupport) }}
                  </div>
                </div>
              </div>
              <div v-if="option.recommended" class="absolute top-2 right-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                ⭐
              </div>
              <div
                v-if="localOptions.errorCorrectionLevel === option.value"
                class="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center"
              >
                <div class="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Performance Optimization Tips -->
      <div class="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <h4 class="font-medium text-yellow-800 mb-2 flex items-center">
          <InformationCircleIcon class="w-4 h-4 mr-2" />
          Performance Tips
        </h4>
        <ul class="text-sm text-yellow-700 space-y-1">
          <li>• Frontend generation is fastest for sizes ≤ 512px</li>
          <li>• Use "Medium" error correction for best compatibility</li>
          <li>• PNG format provides best quality for most uses</li>
          <li>• SVG format requires backend generation</li>
          <li v-if="localOptions.size >= 1024">• Large sizes may benefit from backend processing</li>
          <li v-if="localOptions.source === 'auto'">• Smart routing will optimize automatically</li>
        </ul>
      </div>

      <!-- Compatibility Matrix -->
      <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h4 class="font-medium text-blue-800 mb-3">Feature Compatibility</h4>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-blue-200">
                <th class="text-left py-2">Feature</th>
                <th class="text-center py-2">
                  <DevicePhoneMobileIcon class="w-4 h-4 inline mr-1" />
                  Frontend
                </th>
                <th class="text-center py-2">
                  <ServerIcon class="w-4 h-4 inline mr-1" />
                  Backend
                </th>
              </tr>
            </thead>
            <tbody class="text-xs">
              <tr class="border-b border-blue-100">
                <td class="py-2">Basic QR Generation</td>
                <td class="text-center">✅</td>
                <td class="text-center">✅</td>
              </tr>
              <tr class="border-b border-blue-100">
                <td class="py-2">Instant Preview</td>
                <td class="text-center">✅</td>
                <td class="text-center">❌</td>
              </tr>
              <tr class="border-b border-blue-100">
                <td class="py-2">Logo/Icon Processing</td>
                <td class="text-center">❌</td>
                <td class="text-center">✅</td>
              </tr>
              <tr class="border-b border-blue-100">
                <td class="py-2">SVG Export</td>
                <td class="text-center">❌</td>
                <td class="text-center">✅</td>
              </tr>
              <tr class="border-b border-blue-100">
                <td class="py-2">Batch Processing</td>
                <td class="text-center">⚠️</td>
                <td class="text-center">✅</td>
              </tr>
              <tr>
                <td class="py-2">Large Sizes (>1024px)</td>
                <td class="text-center">⚠️</td>
                <td class="text-center">✅</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Configuration Summary -->
    <div class="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <h4 class="font-medium text-gray-800 mb-3 flex items-center">
        <InformationCircleIcon class="w-4 h-4 mr-2" />
        Current Configuration
      </h4>
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
        <div>
          <span class="text-gray-500">Size:</span>
          <span class="ml-1 font-medium">{{ localOptions.size }}×{{ localOptions.size }}</span>
        </div>
        <div>
          <span class="text-gray-500">Format:</span>
          <span class="ml-1 font-medium">{{ localOptions.format.toUpperCase() }}</span>
        </div>
        <div>
          <span class="text-gray-500">Error Correction:</span>
          <span class="ml-1 font-medium">{{ localOptions.errorCorrectionLevel }}</span>
        </div>
        <div>
          <span class="text-gray-500">Est. Size:</span>
          <span class="ml-1 font-medium">{{ estimatedFileSize }}</span>
        </div>
        <div>
          <span class="text-gray-500">Generation:</span>
          <span class="ml-1 font-medium" :class="{
            'text-green-600': localOptions.source === 'frontend',
            'text-blue-600': localOptions.source === 'backend',
            'text-purple-600': localOptions.source === 'auto'
          }">
            {{ localOptions.source }}
          </span>
        </div>
        <div>
          <span class="text-gray-500">Contrast:</span>
          <span class="ml-1 font-medium" :class="{
            'text-green-600': parseFloat(contrastRatio) >= 7,
            'text-yellow-600': parseFloat(contrastRatio) >= 3 && parseFloat(contrastRatio) < 7,
            'text-red-600': parseFloat(contrastRatio) < 3
          }">
            {{ contrastRatio }}:1
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom color input styling */
input[type="color"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: transparent;
  border: none;
  cursor: pointer;
}

input[type="color"]::-webkit-color-swatch-wrapper {
  padding: 0;
  border-radius: 6px;
  border: 1px solid #d1d5db;
}

input[type="color"]::-webkit-color-swatch {
  border: none;
  border-radius: 6px;
}

input[type="color"]::-moz-color-swatch {
  border: none;
  border-radius: 6px;
}

/* Focus styles for accessibility */
button:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

input:focus {
  outline: none;
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

/* Table styling */
table {
  border-collapse: collapse;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .grid-cols-2 {
    grid-template-columns: 1fr;
  }
  
  .grid-cols-4 {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .grid-cols-3 {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Loading animation for performance indicators */
.performance-indicator {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>